import React, { useMemo, useState } from 'react';
import {
    ChevronDown,
    ChevronUp,
    Clock,
    Eye,
    EyeOff,
    Download,
    Loader2,
    Check,
    X,
    ChevronRight,
    Square,
    CheckSquare,
} from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/pt-br';
import { Link } from 'react-router-dom';
import { generateChapterLink } from '@/utils/utils';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { getChapterPublishDateString, isNewChapterPublished, NovoCapTag } from '@/components/obra/NovoCapChapter';

dayjs.extend(relativeTime);
dayjs.locale('pt-br');

interface ObraChaptersSectionProps {
    obra: any;
    chapters: any[];
    totalChapters: number;
    chaptersAscending: boolean;
    onToggleOrder: () => void;
    isLoadingChapters: boolean;
    chaptersByVolume: { volume: string; chapters: any[] }[] | null;
    collapsedVolumes: Set<string>;
    onToggleVolume: (volume: string) => void;
    isNovel: boolean;
    downloadedChapterIds: Set<number | string>;
    onToggleRead: (chapterId: number, isRead: boolean, e: React.MouseEvent) => void;
    onDownload: (chapter: any, e: React.MouseEvent) => void;
    downloadingChapterId: number | string | null;
    downloadQueue: any[];
    isAuthenticated: boolean;
    onMarkAllRead: (e: React.MouseEvent) => void;
    onUnmarkAllRead: (e: React.MouseEvent) => void;
    showAllChapters?: boolean;
    onToggleShowAll?: () => void;
    hasMoreChapters?: boolean;
    onDownloadAll?: () => void;
    isDownloadingAll?: boolean;
    chapterSelectMode?: boolean;
    selectedChapterIds?: Set<number>;
    onToggleChapterSelectMode?: () => void;
    onToggleChapterSelected?: (capId: number) => void;
    onDownloadSelected?: () => void;
    multiDlWorking?: boolean;
    onSelectAll?: () => void;
    onDeselectAll?: () => void;
}

export const ObraChaptersSection: React.FC<ObraChaptersSectionProps> = ({
    obra,
    chapters,
    totalChapters,
    chaptersAscending,
    onToggleOrder,
    isLoadingChapters,
    chaptersByVolume,
    collapsedVolumes,
    onToggleVolume,
    isNovel,
    downloadedChapterIds,
    onToggleRead,
    onDownload,
    downloadingChapterId,
    downloadQueue,
    isAuthenticated,
    onMarkAllRead,
    onUnmarkAllRead,
    showAllChapters,
    onToggleShowAll,
    hasMoreChapters,
    onDownloadAll,
    isDownloadingAll,
    chapterSelectMode = false,
    selectedChapterIds = new Set<number>(),
    onToggleChapterSelectMode,
    onToggleChapterSelected,
    onDownloadSelected,
    multiDlWorking = false,
    onSelectAll,
    onDeselectAll,
}) => {
    const [searchTerm, setSearchTerm] = useState('');

    const orderOptions = [
        { value: 'desc', label: 'Recentes' },
        { value: 'asc', label: 'Do início' },
    ];

    const handleOrderChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const nextAscending = e.target.value === 'asc';
        if (nextAscending !== chaptersAscending) {
            onToggleOrder();
        }
    };

    const normalizedQuery = searchTerm.trim().toLowerCase();

    const filteredChapters = useMemo(() => {
        if (!normalizedQuery) return chapters;
        return chapters.filter((chapter) => {
            const chapterNumber = String(chapter.cap_num ?? '').toLowerCase();
            const chapterName = String(chapter.cap_nome ?? '').toLowerCase();
            return chapterNumber.includes(normalizedQuery) || chapterName.includes(normalizedQuery);
        });
    }, [chapters, normalizedQuery]);

    const filteredChaptersByVolume = useMemo(() => {
        if (!chaptersByVolume) return null;
        return chaptersByVolume
            .map((group) => ({
                ...group,
                chapters: group.chapters.filter((chapter) => {
                    if (!normalizedQuery) return true;
                    const chapterNumber = String(chapter.cap_num ?? '').toLowerCase();
                    const chapterName = String(chapter.cap_nome ?? '').toLowerCase();
                    return chapterNumber.includes(normalizedQuery) || chapterName.includes(normalizedQuery);
                }),
            }))
            .filter((group) => group.chapters.length > 0);
    }, [chaptersByVolume, normalizedQuery]);

    const toolbarBtn =
        'h-10 shrink-0 rounded-xl border border-white/[0.1] bg-[#1c1c1f] px-3 text-[11px] font-bold text-white/45 transition-colors hover:border-white/15 hover:bg-[#232326] hover:text-white/70';

    const renderChapterItem = (c: any) => {
        const cId = Number(c.cap_id ?? c.id);
        const isDownloaded = downloadedChapterIds.has(Number(cId));
        const isQueued = downloadQueue.some((q) => Number(q.chapterId) === Number(cId));
        const isDownloading = Number(downloadingChapterId) === Number(cId);
        const publishedAt = getChapterPublishDateString(c);
        const isNovo = isNewChapterPublished(publishedAt);
        const read = !!c.lido;
        const rowSelected = selectedChapterIds.has(Number(cId));

        const timeLabel = (() => {
            if (!publishedAt) return 'Recente';
            const date = dayjs(publishedAt);
            if (date.isAfter(dayjs())) return 'Agora';
            return date.fromNow();
        })();

        return (
            <Link
                key={cId}
                to={generateChapterLink(cId)}
                onClick={(e) => {
                    if (chapterSelectMode && onToggleChapterSelected) {
                        e.preventDefault();
                        onToggleChapterSelected(cId);
                    }
                }}
                className={`group flex items-center gap-3 px-4 py-3.5 border-b border-white/[0.06] last:border-b-0 transition-colors ${
                    chapterSelectMode && rowSelected ? 'bg-orange-500/10' : ''
                } ${
                    read
                        ? 'hover:bg-white/[0.02]'
                        : isNovo
                            ? 'bg-emerald-950/25 hover:bg-emerald-950/35'
                            : 'hover:bg-white/[0.03]'
                }`}
            >
                {chapterSelectMode && (
                    <span className="shrink-0 text-orange-400/90" aria-hidden>
                        {rowSelected ? <CheckSquare className="h-5 w-5" /> : <Square className="h-5 w-5 text-white/35" />}
                    </span>
                )}
                <div
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border ${
                        read
                            ? 'border-emerald-500/45 bg-emerald-950/90'
                            : isNovo
                                ? 'border-emerald-500/40 bg-emerald-950/50 text-emerald-200/90'
                                : 'border-white/10 bg-zinc-800/90 text-white/45'
                    }`}
                >
                    {read ? (
                        <Check className="h-5 w-5 text-emerald-400" strokeWidth={2.5} aria-hidden />
                    ) : (
                        <span className="text-sm font-bold tabular-nums">{c.cap_num}</span>
                    )}
                </div>

                <div className="min-w-0 flex-1 flex flex-col justify-center gap-0.5 pr-1">
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                        <span
                            className={`truncate text-sm font-semibold sm:text-[15px] ${
                                read ? 'text-emerald-400' : 'text-white'
                            }`}
                        >
                            {c.cap_nome ?? `Capítulo ${c.cap_num}`}
                        </span>
                        {isNovo && !read && <NovoCapTag />}
                    </div>
                    <div
                        className={`flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] leading-tight ${
                            read ? 'text-emerald-800/75' : 'text-white/35'
                        }`}
                    >
                        <span className="inline-flex items-center gap-1">
                            <Clock size={11} className="shrink-0 opacity-70" aria-hidden />
                            {timeLabel}
                        </span>
                        {read && (
                            <span className="font-semibold uppercase tracking-wide text-emerald-700/85">LIDO</span>
                        )}
                        {chapterSelectMode && (
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-orange-300/90">
                                Toque para marcar
                            </span>
                        )}
                        {!read && c.visualizacoes !== undefined && (
                            <span className="inline-flex items-center gap-1">
                                <Eye size={11} className="shrink-0 opacity-70" aria-hidden />
                                {c.visualizacoes}
                            </span>
                        )}
                    </div>
                </div>

                <div className="flex shrink-0 items-center gap-0.5 self-center text-white/25">
                    {!chapterSelectMode && isAuthenticated && (
                        <button
                            type="button"
                            onClick={(e) => onToggleRead(cId, !!c.lido, e)}
                            className="rounded-lg p-2 transition-colors hover:bg-white/[0.06] hover:text-white/55 z-20"
                            title={read ? 'Marcar como não lido' : 'Marcar como lido'}
                        >
                            {read ? <EyeOff size={17} /> : <Eye size={17} />}
                        </button>
                    )}
                    {!chapterSelectMode && (
                        <button
                            type="button"
                            onClick={(e) => onDownload(c, e)}
                            disabled={isDownloaded || isDownloading || isQueued}
                            className="rounded-lg p-2 transition-colors hover:bg-white/[0.06] hover:text-white/45 disabled:cursor-default disabled:opacity-40 z-20"
                            title={
                                isDownloaded ? 'Capítulo baixado' : isQueued ? 'Na fila' : 'Baixar para offline'
                            }
                        >
                            {isDownloading ? (
                                <Loader2 size={17} className="animate-spin" />
                            ) : isDownloaded ? (
                                <Check size={17} className="text-emerald-500/90" />
                            ) : (
                                <Download size={17} />
                            )}
                        </button>
                    )}
                    {!chapterSelectMode && isDownloaded && (
                        <a
                            href="/offline.html"
                            onClick={(e) => e.stopPropagation()}
                            className="rounded-lg px-2 py-1.5 text-[10px] font-bold uppercase tracking-wide text-emerald-600/90 transition-colors hover:bg-white/[0.06] hover:text-emerald-400 z-20"
                        >
                            Offline
                        </a>
                    )}
                    <ChevronRight
                        className={`hidden h-4 w-4 text-white/15 transition-colors sm:block ${
                            chapterSelectMode ? 'opacity-0' : 'group-hover:text-orange-400/80'
                        }`}
                        aria-hidden
                    />
                </div>
            </Link>
        );
    };

    const listEmpty =
        !isLoadingChapters &&
        (filteredChaptersByVolume
            ? filteredChaptersByVolume.length === 0
            : filteredChapters.length === 0);

    return (
        <section id="capitulos" className="mx-auto max-w-7xl px-4 pb-20 pt-8 md:px-8 md:pt-10">
            <div className="overflow-hidden rounded-2xl border border-white/[0.08] bg-[#151517] shadow-[0_20px_60px_-24px_rgba(0,0,0,0.92)]">
                <div className="border-b border-white/[0.08] bg-[#121214] px-4 pb-4 pt-5">
                    <h2 className="mb-4 flex items-baseline gap-2 text-lg font-bold tracking-tight text-white">
                        Capítulos
                        <span className="text-sm font-semibold text-white/35">{totalChapters}</span>
                    </h2>
                    <div className="flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:items-center">
                        {chapterSelectMode ? (
                            <>
                                <button
                                    type="button"
                                    onClick={onSelectAll}
                                    className={`${toolbarBtn} inline-flex items-center gap-1.5`}
                                    title="Selecionar todos os capítulos"
                                >
                                    <CheckSquare className="h-3.5 w-3.5" strokeWidth={2.25} />
                                    <span>Selecionar todos</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={onDeselectAll}
                                    className={`${toolbarBtn} inline-flex items-center gap-1.5`}
                                    title="Desselecionar todos"
                                >
                                    <Square className="h-3.5 w-3.5" strokeWidth={2.25} />
                                    <span>Desselecionar</span>
                                </button>
                                <div className="hidden sm:block sm:flex-1" />
                                {selectedChapterIds.size > 0 && onDownloadSelected && (
                                    <button
                                        type="button"
                                        disabled={multiDlWorking}
                                        onClick={() => void onDownloadSelected()}
                                        className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-xl border border-orange-500/45 bg-orange-500/15 px-3 text-[11px] font-bold text-orange-200 transition-colors hover:bg-orange-500/25 disabled:opacity-45"
                                    >
                                        {multiDlWorking ? (
                                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                        ) : (
                                            <Download className="h-3.5 w-3.5" strokeWidth={2.25} />
                                        )}
                                        <span>Baixar ({selectedChapterIds.size})</span>
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={onToggleChapterSelectMode}
                                    className={`${toolbarBtn} inline-flex items-center gap-1.5`}
                                    title="Cancelar seleção"
                                >
                                    <X className="h-3.5 w-3.5" strokeWidth={2.5} />
                                    <span>Cancelar</span>
                                </button>
                            </>
                        ) : (
                            <>
                                {isAuthenticated && onToggleChapterSelectMode && (
                                    <button
                                        type="button"
                                        onClick={onToggleChapterSelectMode}
                                        disabled={isDownloadingAll || multiDlWorking}
                                        className={`${toolbarBtn} inline-flex items-center gap-1.5`}
                                        title="Selecionar vários capítulos para baixar offline"
                                    >
                                        <Square className="h-3.5 w-3.5" strokeWidth={2.25} />
                                        <span>Selecionar</span>
                                    </button>
                                )}
                                {isAuthenticated && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={onMarkAllRead}
                                            className={`${toolbarBtn} inline-flex items-center gap-1.5`}
                                            title="Marcar todos como lido"
                                        >
                                            <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                                            <span>Marcar</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={onUnmarkAllRead}
                                            className={`${toolbarBtn} inline-flex items-center gap-1.5`}
                                            title="Desmarcar todos"
                                        >
                                            <X className="h-3.5 w-3.5" strokeWidth={2.5} />
                                            <span>Limpar</span>
                                        </button>
                                    </>
                                )}
                                <Input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Buscar capítulo..."
                                    className="h-10 min-w-0 flex-1 rounded-xl border-white/[0.1] bg-[#1a1a1d] px-3 py-0 text-xs font-semibold text-white placeholder:text-white/30 hover:bg-[#1e1e22] focus:border-orange-500/60 sm:max-w-xs"
                                />
                                <Select
                                    value={chaptersAscending ? 'asc' : 'desc'}
                                    onChange={handleOrderChange}
                                    options={orderOptions}
                                    className="min-w-[132px] sm:ml-auto"
                                    selectClassName="h-10 rounded-xl border-white/[0.1] bg-[#1c1c1f] py-0 text-[11px] font-bold text-white/50 hover:border-white/15 hover:bg-[#232326] hover:text-white/70"
                                />
                            </>
                        )}
                    </div>
                </div>

                <div className="min-h-[100px]">
                    {isLoadingChapters ? (
                        <div className="flex flex-col items-center justify-center gap-4 py-20">
                            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
                            <span className="text-xs font-bold uppercase tracking-[0.2em] text-white/25">
                                Carregando capítulos...
                            </span>
                        </div>
                    ) : listEmpty ? (
                        <p className="px-4 py-14 text-center text-sm text-white/35">Nenhum capítulo encontrado.</p>
                    ) : filteredChaptersByVolume ? (
                        <div className="flex flex-col">
                            {filteredChaptersByVolume.map((group, index) => {
                                const isCollapsed = collapsedVolumes.has(group.volume);
                                return (
                                    <div key={index} className="border-b border-white/[0.06] last:border-b-0">
                                        <button
                                            type="button"
                                            onClick={() => onToggleVolume(group.volume)}
                                            className="flex w-full items-center justify-between bg-[#18181b] px-4 py-2.5 text-left text-xs font-bold uppercase tracking-widest text-white/45 transition-colors hover:bg-[#1c1c20] hover:text-white/70"
                                        >
                                            <span>Volume {group.volume}</span>
                                            {isCollapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
                                        </button>
                                        {!isCollapsed && (
                                            <div className="flex flex-col">{group.chapters.map(renderChapterItem)}</div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="flex flex-col">{filteredChapters.map(renderChapterItem)}</div>
                    )}
                </div>

                {(hasMoreChapters || showAllChapters) && onToggleShowAll && (
                    <div className="border-t border-white/[0.08] bg-[#121214]/80 px-4 py-4">
                        <button
                            type="button"
                            onClick={onToggleShowAll}
                            disabled={chapterSelectMode}
                            className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/[0.08] bg-[#1c1c1f] py-3 text-sm font-bold uppercase tracking-widest text-white/55 transition-colors hover:border-white/15 hover:bg-[#232326] hover:text-white/80 disabled:opacity-40"
                        >
                            {showAllChapters ? (
                                <>
                                    <ChevronUp size={18} />
                                    <span>Mostrar menos</span>
                                </>
                            ) : (
                                <>
                                    <ChevronDown size={18} />
                                    <span>Mostrar todos os capítulos</span>
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
};

